import { h, Host, } from "@stencil/core";
import { Link, router } from "@eventstore-ui/router";
import { theme } from "@eventstore-ui/theme";
import { logger } from "../../../utils/logger";
import { TableHeader } from "../TableHeader";
import { autoExtract } from "../utils/autoExtract";
import { cellClasses } from "../utils/cellClasses";
import { variantMatches } from "../utils/variantMatches";
/** Create a table from data. */
export class Table {
  constructor() {
    this.renderRowGroup = (row, i) => {
      const key = this.loading ? `loading-${i}` : this.getRowKey(row, i);
      return (h("div", { key: key, role: 'rowgroup', class: {
          odd: i % 2 !== 0,
        } }, this.renderRow(row, key, i), !this.loading && this.renderExpansion(h, row, key, i)));
    };
    this.renderRow = (row, key, index) => {
      const data = this.loading ? {} : this.getCellData?.(row);
      if (!data) {
        logger.warn.once('es-table:', `Data not found for cell with row ${row} at index ${index}`);
        return null;
      }
      if (!this.loading && this.linkRowTo) {
        return (h(Link, { url: this.linkRowTo(data), role: 'row', "aria-rowindex": index, class: this.rowClass(data, row), onClick: this.emitRowClick({
            index,
            key,
            row,
            data,
          }), tabindex: '-1' }, this.renderCells(data, row, key, index)));
      }
      return (h("div", { role: 'row', "aria-rowindex": index, class: this.loading ? undefined : this.rowClass(data, key), onClick: this.emitRowClick({
          index,
          key,
          row,
          data,
        }), tabindex: '-1' }, this.renderCells(data, row, key, index)));
    };
    this.renderCells = (data, row, key, index) => this.getColumnGroups().map(([_, cells], groupIndex, groups) => cells.map(([name, cell], cellIndex, cells) => {
      const focusCell = groupIndex === 0 &&
        cellIndex === 0 &&
        (!!this.rowTakesFocus || !!this.linkRowTo) &&
        !this.loading;
      return (h("div", { key: name, role: 'cell', tabindex: focusCell ? '0' : undefined, onKeyDown: focusCell
          ? this.focusCellKeyPress({
            index,
            row,
            key,
            data,
          })
          : undefined, class: cellClasses(cell, data, focusCell, {
          groupIndex,
          groupCount: groups.length,
          cellIndex,
          cellCount: cells.length,
        }) }, this.renderCellContent(data, key, name, cell)));
    }));
    this.renderCellContent = (data, key, name, cell) => {
      if (this.loading) {
        if (cell.expectedLength === 0 && !cell.variance)
          return undefined;
        if (cell.expectedLength === undefined && !cell.title?.length)
          return undefined;
        return (h("es-loading-text", { expectedLength: Math.max(cell.title?.length ?? 0, cell.expectedLength ?? 30), variance: cell.variance }));
      }
      else if (cell.cell) {
        return cell.cell(h, {
          ...(this.extraCellProps?.(key, data) ?? {}),
          key,
          data,
          parent: this.identifier,
        });
      }
      else {
        return autoExtract(data, name);
      }
    };
    this.focusCellKeyPress = (data) => (e) => {
      if (this.loading)
        return;
      if (e.code !== 'Space' && e.code !== 'Enter')
        return;
      this.clickRow.emit(data);
      if (this.linkRowTo) {
        const link = this.linkRowTo(data);
        router.history?.push(link);
      }
    };
    this.emitRowClick = (data) => () => {
      if (this.loading)
        return;
      this.clickRow.emit(data);
    };
    this.getCell = (col) => {
      return this.cells[col] ?? { title: '' };
    };
    this.getColumns = () => {
      if (this.columns)
        return this.columns;
      return Object.keys(this.cells ?? {});
    };
    this.getColumnGroups = () => {
      return this.getColumns().reduce((acc, col) => {
        const cell = this.getCell(col);
        if (cell.variant === 'exclude')
          return acc;
        if (acc.at(-1) != null && acc.at(-1)[0] === cell.group) {
          acc.at(-1)[1].push([col, cell]);
        }
        else {
          acc.push([cell.group, [[col, cell]]]);
        }
        return acc;
      }, []);
    };
    this.gridTemplateColumns = () => this.getColumns().reduce((acc, col) => {
      const cell = this.getCell(col);
      if (variantMatches(cell.variant, 'exclude', 'full-width')) {
        return acc;
      }
      return `${acc} ${cell.width ?? 'auto'}`;
    }, '');
    this.identifier = 'table';
    this.headless = false;
    this.stickyHeader = false;
    this.getCellData = (d) => d;
    this.getRowKey = (row, i) => {
      if (typeof row === 'string')
        return row;
      logger.warn.once('es-table:', `Key not found for row ${row} at index ${i}. Using index as key (unsafe).`, 'Pass the `getRowKey` prop to convert your row into a key.');
      return `${i}`;
    };
    this.cells = undefined;
    this.columns = undefined;
    this.rows = undefined;
    this.linkRowTo = undefined;
    this.rowTakesFocus = undefined;
    this.rowClass = () => undefined;
    this.renderExpansion = () => null;
    this.sort = undefined;
    this.extraCellProps = undefined;
    this.loading = undefined;
    this.loadingRows = 1;
  }
  render() {
    return (h(Host, { role: 'table', style: { gridTemplateColumns: this.gridTemplateColumns() }, "high-contrast": theme.isHighContrast(), dark: theme.isDark(), class: { loading: !!this.loading } }, h(TableHeader, { columnGroups: this.getColumnGroups(), clickSort: this.clickSort, headless: this.headless, sort: this.sort, sticky: this.stickyHeader }), this.loading
      ? Array.from({ length: this.loadingRows }, (_, i) => this.renderRowGroup({}, i))
      : this.rows?.map(this.renderRowGroup)));
  }
  static get is() { return "es-table"; }
  static get originalStyleUrls() {
    return {
      "$": ["es-table.css"]
    };
  }
  static get styleUrls() {
    return {
      "$": ["es-table.css"]
    };
  }
  static get properties() {
    return {
      "identifier": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "Passed to cell renderer as `parent`."
        },
        "attribute": "identifier",
        "reflect": false,
        "defaultValue": "'table'"
      },
      "headless": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "Do not render header."
        },
        "attribute": "headless",
        "reflect": false,
        "defaultValue": "false"
      },
      "stickyHeader": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "Header sticks to scroll parent."
        },
        "attribute": "sticky-header",
        "reflect": false,
        "defaultValue": "false"
      },
      "getCellData": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "(row: any) => any",
          "resolved": "(row: any) => any",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "Sync function for extracting the data from the row. By default, it assumes you passed an array of data as your columns."
        },
        "defaultValue": "(d) => d"
      },
      "getRowKey": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "(row: any, i: number) => string",
          "resolved": "(row: any, i: number) => string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "Sync function for extracting a key from your row data. By default, if the passed rows are strings it will use them directly, otherwise it will warn and use the index."
        },
        "defaultValue": "(row, i) => {\n        if (typeof row === 'string') return row;\n        logger.warn.once(\n            'es-table:',\n            `Key not found for row ${row} at index ${i}. Using index as key (unsafe).`,\n            'Pass the `getRowKey` prop to convert your row into a key.',\n        );\n        return `${i}`;\n    }"
      },
      "cells": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "TableCells<any, any>",
          "resolved": "{ [x: string]: TableCell<any, any>; }",
          "references": {
            "TableCells": {
              "location": "import",
              "path": "../types",
              "id": "src/components/tables/types.ts::TableCells"
            }
          }
        },
        "required": true,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "A record of table cell definitions."
        }
      },
      "columns": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "string[]",
          "resolved": "string[] | undefined",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "The order and keys of the cells to be rendered. If omitted, all cells will be rendered."
        }
      },
      "rows": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "unknown[]",
          "resolved": "unknown[]",
          "references": {}
        },
        "required": true,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "An array of rows to render. Each item in the array is passed to getCellData, to allow passing keys or other identifiers."
        }
      },
      "linkRowTo": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "(data: any) => string",
          "resolved": "((data: any) => string) | undefined",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "A function to calculate a href from the cell data."
        }
      },
      "rowTakesFocus": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean | undefined",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "If rows should be allowed to take focus"
        },
        "attribute": "row-takes-focus",
        "reflect": false
      },
      "rowClass": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "(\n        data: any,\n        row: any,\n    ) => Record<string, boolean> | string | undefined",
          "resolved": "(data: any, row: any) => string | Record<string, boolean> | undefined",
          "references": {
            "Record": {
              "location": "global",
              "id": "global::Record"
            }
          }
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "A function to calculate the class or classes of the row from the cellData."
        },
        "defaultValue": "() => undefined"
      },
      "renderExpansion": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "RenderFunction<\n        [row: any, key: string, i: number]\n    >",
          "resolved": "(h: typeof h, row: any, key: string, i: number) => string | VNode | VNode[] | null",
          "references": {
            "RenderFunction": {
              "location": "import",
              "path": "../../../types",
              "id": "src/types.ts::RenderFunction"
            }
          }
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "Allows rendering a node after the row."
        },
        "defaultValue": "() => null"
      },
      "sort": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "TableSort",
          "resolved": "[key: string, order: SortOrder] | undefined",
          "references": {
            "TableSort": {
              "location": "import",
              "path": "../types",
              "id": "src/components/tables/types.ts::TableSort"
            }
          }
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "How the table is sorted"
        }
      },
      "extraCellProps": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "(key: string, data: any) => Record<string, any>",
          "resolved": "((key: string, data: any) => Record<string, any>) | undefined",
          "references": {
            "Record": {
              "location": "global",
              "id": "global::Record"
            }
          }
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "Pass extra props to cells"
        }
      },
      "loading": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean | undefined",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "Indicates if the loading indicators should be displayed"
        },
        "attribute": "loading",
        "reflect": false
      },
      "loadingRows": {
        "type": "number",
        "mutable": false,
        "complexType": {
          "original": "number",
          "resolved": "number",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "Specifies the number of rows to display when loading is true. Defaults to 1."
        },
        "attribute": "loading-rows",
        "reflect": false,
        "defaultValue": "1"
      }
    };
  }
  static get events() {
    return [{
        "method": "clickRow",
        "name": "clickRow",
        "bubbles": true,
        "cancelable": true,
        "composed": true,
        "docs": {
          "tags": [],
          "text": "Triggered whenever a row is clicked."
        },
        "complexType": {
          "original": "ClickRow<any>",
          "resolved": "ClickRow<any>",
          "references": {
            "ClickRow": {
              "location": "import",
              "path": "../types",
              "id": "src/components/tables/types.ts::ClickRow"
            }
          }
        }
      }, {
        "method": "clickSort",
        "name": "clickSort",
        "bubbles": true,
        "cancelable": true,
        "composed": true,
        "docs": {
          "tags": [],
          "text": "Triggered whenever a sortable header is clicked"
        },
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        }
      }];
  }
}
//# sourceMappingURL=es-table.js.map
